import {
  GetOwnerDetailQuery,
  GetSupplierDetailQuery,
  GetTenantDetailQuery,
  useGetOwnerDetailLazyQuery,
  useGetSupplierDetailLazyQuery,
  useGetTenantDetailLazyQuery,
  UserTypeOption
} from '@/graphql/generated'
import { Col, Container, Row } from 'react-grid-system';
import { useSearchParams } from 'react-router'
import { LoadingSpinner } from '../atoms';
import { useErrorHandlerContext, useUserLanguage } from '@/customHooks';
import { useEffect, useState } from 'react';
import { userTypeToUserTypeOptionMapper } from '@/helpers/userTypeToUserTypeOptionMapper';
import styled from 'styled-components';
import { UserPersonalDataFormControlled } from '../organism';
import { LanguageType } from '@/custom-types';


const StyledContainer = styled(Container)`
  padding-top: ${({ theme }) => theme.spacing.lg};
  padding-bottom: ${({ theme }) => theme.spacing.lg};
`

const StyledForm = styled.div`
  background-color: ${({ theme }) => theme.colors.light};
  padding: 2rem;
`

const SectionWrapper = styled.div`
  border-radius: ${({ theme }) => theme.border.radius.base};
  border-style: solid;
  border-color: ${({ theme }) => theme.colors.border};
  border-width: 1px;
  padding: 2rem;
  margin-top: 1.5rem;
  box-sizing: border-box;
  position: relative;
  margin-bottom: 1.5rem;
  & > span {
    background-color: ${({ theme }) => theme.colors.light};
    position: absolute;
    padding-left: 1rem;
    padding-right: 1rem;
    top: -1.3rem;
    line-height: 0;
  }
`

const titleMapper = (type: string | undefined, language: LanguageType) => {
  const option = {
    en: {
      tenantOutput: 'Tenant',
      ownerOutput: 'Owner',
      supplierOutput: 'Supplier',
    },
    es: {
      tenantOutput: 'Inquilino',
      ownerOutput: 'Propietario',
      supplierOutput: 'Proveedor',
    }
  }

  switch (type) {
    case 'TenantOutput':
      return option[language].tenantOutput

    case 'OwnerOutput':
      return option[language].ownerOutput

    case 'SupplierOutput':
      return option[language].supplierOutput

    default:
      console.warn(`Type ${type} is invalid`)
      return ''
  }
}


export const ClientFinderPage = () => {
  const [searchParams] = useSearchParams()

  const id = searchParams.get('id');
  const userType = searchParams.get('userType')

  const userLanguage = useUserLanguage()

  const [data, setData] = useState<GetTenantDetailQuery['tenant'] | GetOwnerDetailQuery['owner'] | GetSupplierDetailQuery['supplier']>()

  const {error: errorHandler} = useErrorHandlerContext()

  const [getTenant, { loading: tenantLoading }] = useGetTenantDetailLazyQuery()
  const [getOwner, { loading: ownerLoading }] = useGetOwnerDetailLazyQuery()
  const [getSupplier, { loading: supplierLoading }] = useGetSupplierDetailLazyQuery()

  useEffect(() => {
    const userTypeOption = userTypeToUserTypeOptionMapper(userType)

    if (userTypeOption === null) return
    if (id === null) return

    switch (userTypeOption) {
      case UserTypeOption.Tenant:
        getTenant({ variables: { id }})
          .then((response) => setData(response.data?.tenant))
          .catch(errorHandler)
        break
      
      case UserTypeOption.Owner:
        getOwner({ variables: { id }})
          .then((response) => setData(response.data?.owner))
          .catch(errorHandler)
        break

      case UserTypeOption.Supplier:
        getSupplier({ variables: { id }})
          .then((response) => setData(response.data?.supplier))
          .catch(errorHandler)
        break

      default:
        console.warn('Invalid option for UserTypeOption in ClientFinderPage')
        break
    }
  }, [])


  if (tenantLoading || ownerLoading || supplierLoading) {
    return (
      <Row justify='center' align='center' style={{ height: '100%'}}>
        <Col xs='content'>
          <LoadingSpinner />
        </Col>
      </Row>
    )
  }
  return (
    <StyledContainer>
      <StyledForm>
        <SectionWrapper>
          <span><h2>{data ? titleMapper(data?.__typename, userLanguage) : ''}</h2></span>
          {
            data
            ? (
              <UserPersonalDataFormControlled
                onChange={() => {}}
                readOnly
                defaultUserData={{
                  firstName: data.firstName,
                  lastName: data.lastName,
                  email: data.user.email,
                  phoneNumber: data.phoneNumber,
                  secondaryPhoneNumber: data.phoneNumber2 ?? '',
                  documentId: data.documentId,
                  nationality: data.nationality,
                  birthDate: data.birthDate,
                  genderOption: data.genderOption
                }}
              />
            )
            : <p>No information</p>
          }
        </SectionWrapper>
      </StyledForm>
    </StyledContainer>
        
  )
}